import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';

export interface PartnershipProfile {
  uid: string;
  attractivePartnerReason: string;
  hiddenValue: string;
  partnershipGoals: PartnershipGoal[];
  addedValue: string[];
  isOpenForPartnerships: boolean;
  targetAudience: string[];
  brandPositioning: string;
  partnershipHistory: PartnershipHistory[];
  partnershipKit: PartnershipKit;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface PartnershipGoal {
  type: 'leads' | 'events' | 'knowledge' | 'crosspromo' | 'product_bundles';
  description: string;
  priority: 'high' | 'medium' | 'low';
}

export interface PartnershipHistory {
  partnerId: string;
  partnerName: string;
  status: 'contacted' | 'meeting_scheduled' | 'deal_in_progress' | 'completed' | 'declined';
  contactDate: Timestamp;
  notes: string;
  nextAction?: string;
  dealValue?: number;
}

export interface PartnershipKit {
  leadGenerationOpportunities: LeadGenOpportunity[];
  partnerNotes: PartnerNote[];
  emailTemplates: EmailTemplate[];
  archivedEmails: ArchivedEmail[];
}

export interface LeadGenOpportunity {
  id: string;
  type: 'co_hosting' | 'masterclass' | 'challenge' | 'giveaway' | 'webinar' | 'podcast' | 'workshop' | 'event' | 'product_launch' | 'content_collaboration';
  title: string;
  description: string;
  potentialReach: number;
  estimatedValue: number;
  isCompleted: boolean;
  completedDate?: Timestamp;
}

export interface PartnerNote {
  partnerId: string;
  partnerName: string;
  strengths: string[];
  overlap: string[];
  contactMoments: ContactMoment[];
  rating: number; // 1-5 stars
  notes: string;
  tags: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ContactMoment {
  date: Timestamp;
  type: 'email' | 'call' | 'meeting' | 'event';
  notes: string;
  nextAction?: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  isDefault: boolean;
  createdAt: Timestamp;
}

export interface ArchivedEmail {
  id: string;
  partnerId: string;
  partnerName: string;
  subject: string;
  content: string;
  sentDate: Timestamp;
  status: 'sent' | 'opened' | 'replied' | 'no_response';
  responseDate?: Timestamp;
}

export interface PartnershipMatch {
  id: string;
  userId: string;
  partnerId: string;
  matchScore: number;
  matchReasons: string[];
  partnershipPotential: PartnershipPotential;
  suggestedOpportunities: LeadGenOpportunity[];
  status: 'pending' | 'contacted' | 'meeting_scheduled' | 'deal_in_progress' | 'completed' | 'declined';
  createdAt: Timestamp;
  lastInteraction?: Timestamp;
}

export interface PartnershipPotential {
  audienceOverlap: number; // percentage
  goalAlignment: number; // percentage
  valueComplementarity: number; // percentage
  brandCompatibility: number; // percentage
  estimatedDealValue: number;
  suggestedTimeline: 'immediate' | 'short_term' | 'long_term';
}

class PartnerConnectService {
  private readonly PARTNERSHIP_PROFILES_COLLECTION = 'partnershipProfiles';
  private readonly PARTNERSHIP_MATCHES_COLLECTION = 'partnershipMatches';
  private readonly PARTNERSHIP_KITS_COLLECTION = 'partnershipKits';

  /**
   * Create or update partnership profile
   */
  async updatePartnershipProfile(uid: string, profile: Partial<PartnershipProfile>): Promise<void> {
    try {
      const profileRef = doc(db, this.PARTNERSHIP_PROFILES_COLLECTION, uid);
      await updateDoc(profileRef, {
        ...profile,
        updatedAt: Timestamp.now()
      });
    } catch (error: any) {
      // If profile doesn't exist, create it
      if (error.code === 'not-found') {
        await addDoc(collection(db, this.PARTNERSHIP_PROFILES_COLLECTION), {
          uid,
          ...profile,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
      } else {
        throw error;
      }
    }
  }

  /**
   * Get partnership profile
   */
  async getPartnershipProfile(uid: string): Promise<PartnershipProfile | null> {
    try {
      const profileRef = doc(db, this.PARTNERSHIP_PROFILES_COLLECTION, uid);
      const profileDoc = await getDocs(query(collection(db, this.PARTNERSHIP_PROFILES_COLLECTION), where('uid', '==', uid)));
      
      if (profileDoc.docs.length > 0) {
        return profileDoc.docs[0].data() as PartnershipProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting partnership profile:', error);
      return null;
    }
  }

  /**
   * Find strategic partnership matches
   */
  async findPartnershipMatches(userId: string, limit: number = 10): Promise<PartnershipMatch[]> {
    try {
      const userProfile = await this.getPartnershipProfile(userId);
      if (!userProfile || !userProfile.isOpenForPartnerships) {
        throw new Error('User profile not found or not open for partnerships');
      }

      // Get all other partnership profiles
      const allProfiles = await this.getAllPartnershipProfiles(userId);
      const openProfiles = allProfiles.filter(p => p.isOpenForPartnerships);

      // Calculate partnership potential for each profile
      const potentialMatches = openProfiles.map(profile => ({
        profile,
        matchScore: this.calculatePartnershipMatchScore(userProfile, profile),
        matchReasons: this.generatePartnershipMatchReasons(userProfile, profile),
        partnershipPotential: this.calculatePartnershipPotential(userProfile, profile),
        suggestedOpportunities: this.suggestLeadGenOpportunities(userProfile, profile)
      }));

      // Sort by match score and take top matches
      const topMatches = potentialMatches
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, limit);

      // Check for existing matches to avoid duplicates
      const existingMatches = await this.getExistingPartnershipMatches(userId);
      const existingMatchIds = existingMatches.map(match => match.partnerId);

      // Filter out existing matches and create new ones
      const newMatches = topMatches
        .filter(match => !existingMatchIds.includes(match.profile.uid))
        .map(match => ({
          userId,
          partnerId: match.profile.uid,
          matchScore: match.matchScore,
          matchReasons: match.matchReasons,
          partnershipPotential: match.partnershipPotential,
          suggestedOpportunities: match.suggestedOpportunities,
          status: 'pending' as const,
          createdAt: Timestamp.now()
        }));

      // Save new matches to Firestore
      const savedMatches: PartnershipMatch[] = [];
      for (const match of newMatches) {
        const docRef = await addDoc(collection(db, this.PARTNERSHIP_MATCHES_COLLECTION), match);
        savedMatches.push({
          id: docRef.id,
          ...match
        });
      }

      return savedMatches;
    } catch (error) {
      console.error('Error finding partnership matches:', error);
      throw error;
    }
  }

  /**
   * Calculate partnership match score (0-100)
   */
  private calculatePartnershipMatchScore(profile1: PartnershipProfile, profile2: PartnershipProfile): number {
    let score = 0;
    let totalWeight = 0;

    // Goal alignment (weight: 30%)
    const goalWeight = 30;
    const goalScore = this.calculateGoalAlignment(profile1.partnershipGoals, profile2.partnershipGoals);
    score += goalScore * goalWeight;
    totalWeight += goalWeight;

    // Audience overlap (weight: 25%)
    const audienceWeight = 25;
    const audienceScore = this.calculateAudienceOverlap(profile1.targetAudience, profile2.targetAudience);
    score += audienceScore * audienceWeight;
    totalWeight += audienceWeight;

    // Value complementarity (weight: 25%)
    const valueWeight = 25;
    const valueScore = this.calculateValueComplementarity(profile1.addedValue, profile2.addedValue);
    score += valueScore * valueWeight;
    totalWeight += valueWeight;

    // Brand compatibility (weight: 20%)
    const brandWeight = 20;
    const brandScore = this.calculateBrandCompatibility(profile1.brandPositioning, profile2.brandPositioning);
    score += brandScore * brandWeight;
    totalWeight += brandWeight;

    return Math.round(score / totalWeight);
  }

  /**
   * Calculate goal alignment score
   */
  private calculateGoalAlignment(goals1: PartnershipGoal[], goals2: PartnershipGoal[]): number {
    if (goals1.length === 0 || goals2.length === 0) return 0;

    const goalTypes1 = goals1.map(g => g.type);
    const goalTypes2 = goals2.map(g => g.type);

    const intersection = goalTypes1.filter((type) => goalTypes2.includes(type));
    const union = Array.from(new Set([...goalTypes1, ...goalTypes2]));

    return (intersection.length / union.length) * 100;
  }

  /**
   * Calculate audience overlap score
   */
  private calculateAudienceOverlap(audience1: string[], audience2: string[]): number {
    if (audience1.length === 0 || audience2.length === 0) return 0;

    const normalized1 = audience1.map(a => a.toLowerCase().trim());
    const normalized2 = audience2.map(a => a.toLowerCase().trim());

    const intersection = normalized1.filter(audience => normalized2.includes(audience));
    const union = Array.from(new Set([...normalized1, ...normalized2]));

    return (intersection.length / union.length) * 100;
  }

  /**
   * Calculate value complementarity score
   */
  private calculateValueComplementarity(values1: string[], values2: string[]): number {
    if (values1.length === 0 || values2.length === 0) return 50; // Neutral score

    const normalized1 = values1.map(v => v.toLowerCase().trim());
    const normalized2 = values2.map(v => v.toLowerCase().trim());

    // Check for complementary values (different but related)
    const complementaryMatches = normalized1.filter(value1 => 
      normalized2.some(value2 => 
        this.areValuesComplementary(value1, value2)
      )
    );

    return Math.min((complementaryMatches.length / normalized1.length) * 100, 100);
  }

  /**
   * Check if two values are complementary
   */
  private areValuesComplementary(value1: string, value2: string): boolean {
    const complementaryPairs = [
      ['marketing', 'sales'],
      ['design', 'development'],
      ['content', 'distribution'],
      ['strategy', 'execution'],
      ['local', 'international'],
      ['b2b', 'b2c'],
      ['technology', 'consulting'],
      ['training', 'coaching']
    ];

    return complementaryPairs.some(pair => 
      (pair[0] === value1 && pair[1] === value2) ||
      (pair[1] === value1 && pair[0] === value2)
    );
  }

  /**
   * Calculate brand compatibility score
   */
  private calculateBrandCompatibility(brand1: string, brand2: string): number {
    if (!brand1 || !brand2) return 50;

    const brand1Lower = brand1.toLowerCase();
    const brand2Lower = brand2.toLowerCase();

    // Check for similar brand positioning keywords
    const brandKeywords = ['premium', 'affordable', 'innovative', 'traditional', 'modern', 'classic', 'disruptive', 'established'];
    
    const keywords1 = brandKeywords.filter(keyword => brand1Lower.includes(keyword));
    const keywords2 = brandKeywords.filter(keyword => brand2Lower.includes(keyword));

    if (keywords1.length === 0 || keywords2.length === 0) return 50;

    const intersection = keywords1.filter(keyword => keywords2.includes(keyword));
    return intersection.length > 0 ? 80 : 30;
  }

  /**
   * Calculate partnership potential
   */
  private calculatePartnershipPotential(profile1: PartnershipProfile, profile2: PartnershipProfile): PartnershipPotential {
    const audienceOverlap = this.calculateAudienceOverlap(profile1.targetAudience, profile2.targetAudience);
    const goalAlignment = this.calculateGoalAlignment(profile1.partnershipGoals, profile2.partnershipGoals);
    const valueComplementarity = this.calculateValueComplementarity(profile1.addedValue, profile2.addedValue);
    const brandCompatibility = this.calculateBrandCompatibility(profile1.brandPositioning, profile2.brandPositioning);

    // Estimate deal value based on match quality
    const estimatedDealValue = Math.round((audienceOverlap + goalAlignment + valueComplementarity + brandCompatibility) * 100);

    // Determine timeline based on urgency and alignment
    const urgencyScore = profile1.partnershipGoals.filter(g => g.priority === 'high').length;
    const suggestedTimeline = urgencyScore > 1 ? 'immediate' : goalAlignment > 70 ? 'short_term' : 'long_term';

    return {
      audienceOverlap,
      goalAlignment,
      valueComplementarity,
      brandCompatibility,
      estimatedDealValue,
      suggestedTimeline
    };
  }

  /**
   * Suggest lead generation opportunities
   */
  private suggestLeadGenOpportunities(profile1: PartnershipProfile, profile2: PartnershipProfile): LeadGenOpportunity[] {
    const opportunities: LeadGenOpportunity[] = [];

    // Co-hosting opportunity
    if (this.hasOverlappingGoals(profile1, profile2, ['leads', 'events'])) {
      opportunities.push({
        id: `co_hosting_${Date.now()}`,
        type: 'co_hosting',
        title: 'Co-hosted Webinar',
        description: 'Organiseer een gezamenlijke webinar om leads te genereren',
        potentialReach: Math.round((profile1.targetAudience.length + profile2.targetAudience.length) * 0.3),
        estimatedValue: 5000,
        isCompleted: false
      });
    }

    // Masterclass opportunity
    if (this.hasComplementaryValues(profile1, profile2)) {
      opportunities.push({
        id: `masterclass_${Date.now()}`,
        type: 'masterclass',
        title: 'Expert Masterclass',
        description: 'Creëer een masterclass die jullie expertise combineert',
        potentialReach: Math.round((profile1.targetAudience.length + profile2.targetAudience.length) * 0.2),
        estimatedValue: 3000,
        isCompleted: false
      });
    }

    // Challenge opportunity
    if (this.hasOverlappingGoals(profile1, profile2, ['leads', 'knowledge'])) {
      opportunities.push({
        id: `challenge_${Date.now()}`,
        type: 'challenge',
        title: '30-Day Challenge',
        description: 'Lanceer een gezamenlijke challenge om engagement te verhogen',
        potentialReach: Math.round((profile1.targetAudience.length + profile2.targetAudience.length) * 0.4),
        estimatedValue: 2000,
        isCompleted: false
      });
    }

    return opportunities;
  }

  /**
   * Check if profiles have overlapping goals
   */
  private hasOverlappingGoals(profile1: PartnershipProfile, profile2: PartnershipProfile, goalTypes: string[]): boolean {
    const goals1 = profile1.partnershipGoals.map(g => g.type);
    const goals2 = profile2.partnershipGoals.map(g => g.type);
    
    return goalTypes.some(type => goals1.includes(type as any) && goals2.includes(type as any));
  }

  /**
   * Check if profiles have complementary values
   */
  private hasComplementaryValues(profile1: PartnershipProfile, profile2: PartnershipProfile): boolean {
    return this.calculateValueComplementarity(profile1.addedValue, profile2.addedValue) > 60;
  }

  /**
   * Generate partnership match reasons
   */
  private generatePartnershipMatchReasons(profile1: PartnershipProfile, profile2: PartnershipProfile): string[] {
    const reasons: string[] = [];

    // Goal alignment
    const goalAlignment = this.calculateGoalAlignment(profile1.partnershipGoals, profile2.partnershipGoals);
    if (goalAlignment > 50) {
      reasons.push(`Gedeelde partnership doelen (${Math.round(goalAlignment)}% overlap)`);
    }

    // Audience overlap
    const audienceOverlap = this.calculateAudienceOverlap(profile1.targetAudience, profile2.targetAudience);
    if (audienceOverlap > 30) {
      reasons.push(`Overlappende doelgroep (${Math.round(audienceOverlap)}% overlap)`);
    }

    // Value complementarity
    const valueComplementarity = this.calculateValueComplementarity(profile1.addedValue, profile2.addedValue);
    if (valueComplementarity > 60) {
      reasons.push('Complementaire expertise en waarde');
    }

    // Brand compatibility
    const brandCompatibility = this.calculateBrandCompatibility(profile1.brandPositioning, profile2.brandPositioning);
    if (brandCompatibility > 70) {
      reasons.push('Vergelijkbare merkpositionering');
    }

    return reasons.slice(0, 3);
  }

  /**
   * Get user's partnership matches
   */
  async getUserPartnershipMatches(userId: string): Promise<PartnershipMatch[]> {
    try {
      const q = query(
        collection(db, this.PARTNERSHIP_MATCHES_COLLECTION),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PartnershipMatch[];
    } catch (error) {
      console.error('Error getting partnership matches:', error);
      throw error;
    }
  }

  /**
   * Update partnership match status
   */
  async updatePartnershipMatchStatus(matchId: string, status: PartnershipMatch['status']): Promise<void> {
    try {
      const matchRef = doc(db, this.PARTNERSHIP_MATCHES_COLLECTION, matchId);
      await updateDoc(matchRef, {
        status,
        lastInteraction: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating partnership match status:', error);
      throw error;
    }
  }

  /**
   * Add partner note
   */
  async addPartnerNote(userId: string, partnerNote: Omit<PartnerNote, 'createdAt' | 'updatedAt'>): Promise<void> {
    try {
      const kitRef = doc(db, this.PARTNERSHIP_KITS_COLLECTION, userId);
      await updateDoc(kitRef, {
        partnerNotes: arrayUnion({
          ...partnerNote,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        })
      });
    } catch (error) {
      // If kit doesn't exist, create it
      await addDoc(collection(db, this.PARTNERSHIP_KITS_COLLECTION), {
        userId,
        partnerNotes: [{
          ...partnerNote,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }],
        leadGenerationOpportunities: [],
        emailTemplates: [],
        archivedEmails: []
      });
    }
  }

  /**
   * Get partnership kit
   */
  async getPartnershipKit(userId: string): Promise<PartnershipKit | null> {
    try {
      const kitRef = doc(db, this.PARTNERSHIP_KITS_COLLECTION, userId);
      const kitDoc = await getDocs(query(collection(db, this.PARTNERSHIP_KITS_COLLECTION), where('userId', '==', userId)));
      
      if (kitDoc.docs.length > 0) {
        return kitDoc.docs[0].data().partnershipKit as PartnershipKit;
      }
      return null;
    } catch (error) {
      console.error('Error getting partnership kit:', error);
      return null;
    }
  }

  // Helper methods
  private async getAllPartnershipProfiles(excludeUserId?: string): Promise<PartnershipProfile[]> {
    const querySnapshot = await getDocs(collection(db, this.PARTNERSHIP_PROFILES_COLLECTION));
    return querySnapshot.docs
      .map(doc => ({ ...doc.data(), uid: doc.data().uid || doc.id } as PartnershipProfile))
      .filter(profile => !excludeUserId || profile.uid !== excludeUserId);
  }

  private async getExistingPartnershipMatches(userId: string): Promise<PartnershipMatch[]> {
    const q = query(
      collection(db, this.PARTNERSHIP_MATCHES_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as PartnershipMatch[];
  }
}

export const partnerConnectService = new PartnerConnectService();
